// Encrypt (with the network key) + upload one image/video, returning a media item
// {path, type, iv, thumb, thumb_iv}. Ported from CreatePost so the Hearth composer
// produces the exact same format SecureMedia can decrypt/play.
import api from "./api";
import { buf2b64, b642buf, MEDIA_CHUNK_MAGIC } from "./crypto";

const CHUNK_SIZE = 4 * 1024 * 1024;
const CHUNK_THRESHOLD = 5 * 1024 * 1024;

// iOS/iPadOS often hands us files with an empty or odd MIME type, so detect
// video by extension too. Misclassifying a video as an image sends it down the
// whole-file-in-memory image path and crashes/reloads the tab.
const VIDEO_EXTS = ["mp4", "mov", "m4v", "webm", "avi", "mkv", "3gp", "3g2", "mpg", "mpeg", "hevc", "qt"];
export function isVideoFile(file) {
  if (!file) return false;
  if ((file.type || "").toLowerCase().startsWith("video")) return true;
  const name = (file.name || "").toLowerCase();
  const ext = name.includes(".") ? name.split(".").pop() : "";
  return VIDEO_EXTS.includes(ext);
}

// Shared media limits, kept identical across the Signal feed and the Hearth.
export const MAX_SLIDES = 10;
export const MAX_VIDEO_SECONDS = 900; // 15 minutes
export const MAX_VIDEO_BYTES = 500 * 1024 * 1024; // 500 MB

// Read a video file's duration (seconds) from its metadata. Never hangs.
export function getVideoDuration(file) {
  return new Promise((resolve) => {
    let done = false;
    const finish = (val, v) => { if (done) return; done = true; try { if (v) URL.revokeObjectURL(v.src); } catch { /* noop */ } resolve(val); };
    try {
      const v = document.createElement("video");
      v.preload = "metadata";
      v.onloadedmetadata = () => finish(v.duration || 0, v);
      v.onerror = () => finish(0, v);
      v.src = URL.createObjectURL(file);
      setTimeout(() => finish(0, v), 8000); // some mobile browsers never fire the event
    } catch { resolve(0); }
  });
}

// Returns a friendly error string if the video is too large/long, else null.
export async function validateVideoFile(file) {
  if (!isVideoFile(file)) return null;
  if (file.size > MAX_VIDEO_BYTES) {
    return `"${file.name}" is too large. Please keep videos under 500 MB (for longer clips, record at 1080p instead of 4K).`;
  }
  const dur = await getVideoDuration(file);
  if (dur > MAX_VIDEO_SECONDS + 1) {
    return `"${file.name}" is ${Math.round(dur)}s. Please keep videos under 15 minutes for now.`;
  }
  return null;
}

export async function compressImage(file, max = 2000) {
  try {
    const bitmap = await createImageBitmap(file);
    let { width, height } = bitmap;
    if (width > max || height > max) {
      const s = Math.min(max / width, max / height);
      width = Math.round(width * s); height = Math.round(height * s);
    }
    const canvas = document.createElement("canvas");
    canvas.width = width; canvas.height = height;
    canvas.getContext("2d").drawImage(bitmap, 0, 0, width, height);
    const blob = await new Promise((r) => canvas.toBlob(r, "image/jpeg", 0.85));
    return blob || file;
  } catch { return file; }
}

async function makeThumbBytes(file) {
  try {
    const bitmap = await createImageBitmap(file);
    const max = 24;
    const s = Math.min(max / bitmap.width, max / bitmap.height, 1);
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.round(bitmap.width * s));
    canvas.height = Math.max(1, Math.round(bitmap.height * s));
    canvas.getContext("2d").drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise((r) => canvas.toBlob(r, "image/jpeg", 0.6));
    return blob ? await blob.arrayBuffer() : null;
  } catch { return null; }
}

async function postChunk(fd) {
  let lastErr;
  for (let attempt = 0; attempt < 3; attempt++) {
    try {
      return await api.post("/upload/chunk", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 120000 });
    } catch (e) {
      lastErr = e;
      await new Promise((r) => setTimeout(r, 800 * (attempt + 1)));
    }
  }
  throw lastErr;
}

async function uploadBlob(blob, filename, enc, onPct) {
  if (blob.size <= CHUNK_THRESHOLD) {
    const fd = new FormData();
    fd.append("file", blob, filename);
    fd.append("enc", enc ? "true" : "false");
    const { data } = await api.post("/upload", fd, {
      headers: { "Content-Type": "multipart/form-data" },
      onUploadProgress: (e) => { if (onPct && e.total) onPct(Math.round((e.loaded / e.total) * 100)); },
    });
    return data.path;
  }
  const uploadId = window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  const total = Math.ceil(blob.size / CHUNK_SIZE);
  for (let i = 0; i < total; i++) {
    const part = blob.slice(i * CHUNK_SIZE, Math.min((i + 1) * CHUNK_SIZE, blob.size));
    const fd = new FormData();
    fd.append("upload_id", uploadId); fd.append("index", String(i)); fd.append("chunk", part, "chunk");
    await postChunk(fd);
    if (onPct) onPct(Math.round(((i + 1) / total) * 100));
  }
  const { data } = await api.post("/upload/complete", {
    upload_id: uploadId, filename, content_type: blob.type || "application/octet-stream", enc,
  });
  return data.path;
}

async function encryptedVideoUpload(file, encryptNetBytes, onPct) {
  const uploadId = window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  const size = file.size;
  let index = 0;
  for (let start = 0; start < size; start += CHUNK_SIZE) {
    const buf = await file.slice(start, Math.min(start + CHUNK_SIZE, size)).arrayBuffer();
    const { ctBuf, ivB64 } = await encryptNetBytes(buf);
    const header = new ArrayBuffer(4);
    new DataView(header).setUint32(0, ctBuf.byteLength);
    const iv = b642buf(ivB64);
    const parts = index === 0 ? [MEDIA_CHUNK_MAGIC, header, iv, ctBuf] : [header, iv, ctBuf];
    const fd = new FormData();
    fd.append("upload_id", uploadId); fd.append("index", String(index)); fd.append("chunk", new Blob(parts), "chunk");
    await postChunk(fd);
    index++;
    if (onPct) onPct(Math.round((Math.min(start + CHUNK_SIZE, size) / size) * 100));
  }
  const { data } = await api.post("/upload/complete", {
    upload_id: uploadId, filename: "enc.bin", content_type: "application/octet-stream", enc: true,
  });
  return data.path;
}

// Encrypt one file with an explicit AES key function (used by per-Beacon
// subscriber content). encryptBytes: (ArrayBuffer) => { ctBuf, ivB64 }.
// Videos are framed/chunked; images & audio are single encrypted blobs.
export async function encryptAndUploadWithKey(file, encryptBytes, type, onPct, maxPx) {
  if (type === "video") {
    const path = await encryptedVideoUpload(file, encryptBytes, onPct);
    return { path, type: "video", iv: null };
  }
  const isImg = type === "image" || type === "artwork";
  const src = isImg ? await compressImage(file, maxPx) : file;
  const { ctBuf, ivB64 } = await encryptBytes(await src.arrayBuffer());
  const path = await uploadBlob(new Blob([ctBuf]), "enc.bin", true, onPct);
  let thumb = null, thumb_iv = null;
  if (isImg) {
    try {
      const tb = await makeThumbBytes(file);
      if (tb) { const et = await encryptBytes(tb); if (et?.ctBuf) { thumb = buf2b64(et.ctBuf); thumb_iv = et.ivB64; } }
    } catch { /* optional */ }
  }
  return { path, type, iv: ivB64, thumb, thumb_iv };
}

export async function encryptAndUpload(file, { encryptNetBytes, hasNetwork, onPct }) {
  const isVideo = isVideoFile(file);
  const type = isVideo ? "video" : "image";
  const enc = !!(hasNetwork && hasNetwork());
  if (!enc) {
    const path = await uploadBlob(isVideo ? file : await compressImage(file), file.name, false, onPct);
    return { path, type, iv: null };
  }
  if (isVideo) {
    const path = await encryptedVideoUpload(file, encryptNetBytes, onPct);
    return { path, type: "video", iv: null };
  }
  const src = await compressImage(file);
  const { ctBuf, ivB64 } = await encryptNetBytes(await src.arrayBuffer());
  const path = await uploadBlob(new Blob([ctBuf]), "enc.bin", true, onPct);
  let thumb = null, thumb_iv = null;
  try {
    const tb = await makeThumbBytes(file);
    if (tb) { const et = await encryptNetBytes(tb); if (et?.ctBuf) { thumb = buf2b64(et.ctBuf); thumb_iv = et.ivB64; } }
  } catch { /* optional */ }
  return { path, type: "image", iv: ivB64, thumb, thumb_iv };
}
